import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TextField, Button, Alert,
  TablePagination, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Grid, Card, CardContent, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText, Accordion,
  AccordionSummary, AccordionDetails, CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import API_CONFIG, { getApiUrl } from '../../config/api';

const SurveysManagement = () => {
  const [surveys, setSurveys] = useState([]);
  const [questions, setQuestions] = useState({});
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSurvey, setMenuSurvey] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalSurveys, setTotalSurveys] = useState(0);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsData, setQuestionsData] = useState({});

  const navigate = useNavigate();

  const fetchSurveys = async (pageNum = 0, pageSize = 50, fromDate = '', toDate = '') => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching surveys efficiently...');

      // Build query parameters
      const params = new URLSearchParams({
        page: pageNum,
        limit: pageSize,
        admin_password: 'Admin@123!!!'
      });

      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const apiUrl = getApiUrl(API_CONFIG.ENDPOINTS.ADMIN.SURVEYS) + '?' + params.toString();
      console.log('API URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: 'Admin@123!!!' })
      });

      const data = await response.json();
      console.log('Surveys data:', data);

      if (data.error) throw new Error(data.error);

      if (data.status === 'success') {
        setSurveys(data.surveys || []);
        setTotalSurveys(data.total || 0);

        // Questions data is now just counts from the API
        const questionsData = {};
        data.surveys.forEach(survey => {
          questionsData[survey.id] = survey.question_count || 0;
        });
        setQuestions(questionsData);

        // If no surveys found, show a helpful message
        if (data.surveys.length === 0) {
          setError('No surveys found. This could be because: 1) No users have created surveys yet, 2) API endpoints are not accessible, or 3) Database connection issues.');
        }
      } else {
        throw new Error('Failed to fetch surveys');
      }
    } catch (e) {
      console.error('Error in fetchSurveys:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodaysSurveys = () => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setDateFrom(todayStr);
    setDateTo(todayStr);
    setPage(0);
    fetchSurveys(0, rowsPerPage, todayStr, todayStr);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuClick = (event, survey) => {
    setAnchorEl(event.currentTarget);
    setMenuSurvey(survey);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSurvey(null);
  };

  const handleViewSurvey = (survey) => {
    setSelectedSurvey(survey);
    setSurveyDialogOpen(true);
    handleMenuClose();
  };

  const handleEditSurvey = (survey) => {
    // Navigate to survey edit or open edit dialog
    console.log('Edit survey:', survey);
    handleMenuClose();
  };

  const handleDeleteSurvey = (survey) => {
    // Implement survey deletion
    console.log('Delete survey:', survey);
    handleMenuClose();
  };

  const handleViewResponses = (survey) => {
    // Navigate to responses page for this survey
    navigate(`/admin/responses?survey_id=${survey.id}`);
    handleMenuClose();
  };

  const fetchSurveyQuestions = async (surveyId) => {
    setLoadingQuestions(true);
    try {
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.LIST) + `?survey_id=${surveyId}`);
      const data = await response.json();

      if (data.status === 'success') {
        setQuestionsData(prev => ({
          ...prev,
          [surveyId]: data.questions || []
        }));
      } else {
        setQuestionsData(prev => ({
          ...prev,
          [surveyId]: []
        }));
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestionsData(prev => ({
        ...prev,
        [surveyId]: []
      }));
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleQuestionsClick = (survey) => {
    if (!questionsData[survey.id]) {
      fetchSurveyQuestions(survey.id);
    }
    setSelectedSurvey(survey);
    setSurveyDialogOpen(true);
  };

  useEffect(() => {
    fetchTodaysSurveys();
  }, []);

  // Global search filter
  const filteredSurveys = surveys.filter(survey => {
    if (!search) return true;
    const searchStr = search.toLowerCase();
    return (
      survey.name.toLowerCase().includes(searchStr) ||
      survey.user_email.toLowerCase().includes(searchStr) ||
      survey.user_name?.toLowerCase().includes(searchStr) ||
      survey.status.toLowerCase().includes(searchStr)
    );
  });

  // Sort surveys
  const sortedSurveys = [...filteredSurveys].sort((a, b) => {
    let aValue, bValue;

    switch (orderBy) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'user_email':
        aValue = a.user_email || '';
        bValue = b.user_email || '';
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'created_at':
      default:
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
    }

    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'published': return 'Published';
      case 'draft': return 'Draft';
      case 'archived': return 'Archived';
      default: return status;
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Surveys Management
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              placeholder="Search surveys..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" onClick={fetchSurveys} fullWidth>
              Refresh
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchTodaysSurveys}
              disabled={loading}
              fullWidth
            >
              Today's Surveys
            </Button>
          </Grid>
        </Grid>

        {/* Date Filters */}
        <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              onClick={() => {
                setPage(0);
                fetchSurveys(0, rowsPerPage, dateFrom, dateTo);
              }}
              disabled={loading}
              fullWidth
              sx={{ height: '56px' }}
            >
              Filter by Date
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setPage(0);
                fetchTodaysSurveys();
              }}
              disabled={loading}
              fullWidth
              sx={{ height: '56px' }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading surveys... Please wait while we fetch data from all users.
        </Alert>
      )}

      {/* Surveys Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Survey Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'user_email'}
                  direction={orderBy === 'user_email' ? order : 'asc'}
                  onClick={() => handleSort('user_email')}
                >
                  Owner
                </TableSortLabel>
              </TableCell>
              <TableCell>Questions</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleSort('created_at')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSurveys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" color="textSecondary">
                    No surveys found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {error || 'No users have created surveys yet, or there may be an issue with the API connection.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : sortedSurveys.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(survey => (
              <TableRow key={survey.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {survey.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {survey.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {survey.user_name || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {survey.user_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${questions[survey.id] || 0} questions`}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => handleQuestionsClick(survey)}
                    color={questionsData[survey.id] ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(survey.status)}
                    size="small"
                    color={getStatusColor(survey.status)}
                  />
                </TableCell>
                <TableCell>
                  {new Date(survey.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <IconButton onClick={(e) => handleMenuClick(e, survey)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalSurveys}
          page={page}
          onPageChange={(e, newPage) => {
            setPage(newPage);
            fetchSurveys(newPage, rowsPerPage, dateFrom, dateTo);
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
            fetchSurveys(0, parseInt(e.target.value, 10), dateFrom, dateTo);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Survey Details Dialog */}
      <Dialog open={surveyDialogOpen} onClose={() => setSurveyDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedSurvey ? `${selectedSurvey.name} - Survey Details` : 'Survey Details'}
        </DialogTitle>
        <DialogContent>
          {selectedSurvey && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Survey Information</Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography><strong>Name:</strong> {selectedSurvey.name}</Typography>
                      <Typography><strong>Owner:</strong> {selectedSurvey.user_name} ({selectedSurvey.user_email})</Typography>
                      <Typography><strong>Status:</strong> {getStatusText(selectedSurvey.status)}</Typography>
                      <Typography><strong>Created:</strong> {new Date(selectedSurvey.created_at).toLocaleString()}</Typography>
                      <Typography><strong>Updated:</strong> {new Date(selectedSurvey.updated_at).toLocaleString()}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Questions ({questions[selectedSurvey.id] || 0})</Typography>
                    {loadingQuestions ? (
                      <CircularProgress size={24} />
                    ) : questionsData[selectedSurvey.id]?.length > 0 ? (
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {questionsData[selectedSurvey.id].map((question, index) => (
                          <Accordion key={question.id} sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2">
                                Q{index + 1}: {question.question_text}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography variant="caption" color="textSecondary">
                                Type: {question.question_type} | Required: {question.is_required ? 'Yes' : 'No'}
                              </Typography>
                              {question.options && (
                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                  Options: {JSON.stringify(question.options)}
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {questionsData[selectedSurvey.id]?.length === 0 ? 'No questions found' : 'Click on questions count to load details'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSurveyDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Survey Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewSurvey(menuSurvey)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleViewResponses(menuSurvey)}>
          <ListItemIcon>
            <QuestionAnswerIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Responses</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEditSurvey(menuSurvey)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Survey</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteSurvey(menuSurvey)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Survey</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SurveysManagement;